import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import AppContext from 'context/Context';
import { toast } from 'react-toastify';
import BookingInForm from './BookingInForm';
import BookingInTable from './BookingInTable';
import BookingInPhotoStep from './BookingInPhotoStep';
import BookingInIncorrectStep from './BookingInIncorrectStep';
import { swalConfirm } from 'components/utilities/SwalToastUtil';
import {
  getWpoLines,
  submitBookingIn,
  sendMailDiscrepancy,
  saveIncorrectItems
} from 'services/stock-location/bookingInService';

const STEPS = {
  BOOKING: 0,
  PHOTO_DN: 1,
  PHOTO_DISC: 2,
  PHOTO_INC: 3,
  COMPLETED: 4
};

const APPS = {
  DELIVERY_NOTE: 'delivery.note.sls.app',
  DISCREPANCY: 'discrepancy.sls.app',
  INCORRECT_ITEM: 'incorrect.item.sls.app'
};

const buildPhotoPrefix = (basePrefix, currentGrnNo) =>
  currentGrnNo ? `${basePrefix}/${currentGrnNo}` : basePrefix;

const hasPopulatedQty = value =>
  value !== '' && value !== null && value !== undefined && !Number.isNaN(Number(value));

const hasReceivedQty = value => hasPopulatedQty(value) && Number(value) !== 0;

const BookingInModal = ({ isShow, handleClose, wpoData }) => {
  const { config: { isDark } } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.BOOKING);
  const [grnNo, setGrnNo] = useState(null);
  const [recordId, setRecordId] = useState(null);
  const [poId, setPoId] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [validated, setValidated] = useState(false);
  const [hasDiscrepancy, setHasDiscrepancy] = useState(false);
  const discrepancyInfoRef = useRef({ has: false, summary: '' });
  const grnInfoRef = useRef({ grnNo: null, recordId: null });
  const submissionItemsRef = useRef([]);
  const [submissionItems, setSubmissionItems] = useState([]);
  const [formData, setFormData] = useState({
    deliveryNoteRef: '',
    dateReceived: new Date(),
    globalBinBarcode: ''
  });

  const extractGrnNo = (data) => {
    if (!data) return '';
    if (typeof data === 'string' || typeof data === 'number') return String(data);
    if (typeof data === 'object') {
      return (
        data.grnNo ||
        data.GrnNo ||
        data.GRNNo ||
        data.grn ||
        data.Grn ||
        ''
      );
    }
    return '';
  };

  useEffect(() => {
    if (isShow) {
      if (wpoData?.wpoNo) {
        loadWpoLines(wpoData.wpoNo);
      }
    } else {
      setTableData([]);
      setValidated(false);
      setCurrentStep(STEPS.BOOKING);
      setGrnNo(null);
      setRecordId(null);
      setHasDiscrepancy(false);
      discrepancyInfoRef.current = { has: false, summary: '' };
      grnInfoRef.current = { grnNo: null, recordId: null };
      submissionItemsRef.current = [];
      setSubmissionItems([]);
      setFormData({
        deliveryNoteRef: '',
        dateReceived: new Date(),
        globalBinBarcode: ''
      });
    }
  }, [isShow, wpoData]);


  const loadWpoLines = async (wpoNo) => {
    setIsLoading(true);
    try {
      const res = await getWpoLines(wpoNo);
      const lines = res.data || [];

      if (lines.length > 0) {
        setPoId(lines[0].poId);
      }

      const mappedData = lines.map((item, idx) => ({
        ...item,
        id: item.poItemId || idx,
        emplasCode: item.productCode,
        supplierCode: item.supplierPartNo,
        code: item.productCode,
        description: item.description,
        balanceDue: item.orderQty - (item.receivedQty || 0),
        deliveryNoteQty: '',
        actualReceived: '',
        binBarcode: item.binBarCode || ''
      }));

      setTableData(mappedData);
    } catch (err) {
      toast.error('Failed to load WPO lines');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, dateReceived: date }));
  };

  const handleRowChange = useCallback((index, field, value) => {
    setTableData(prev =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;

        if (field === 'deliveryNoteQty') {
          return {
            ...row,
            deliveryNoteQty: value,
            actualReceived: value
          };
        }

        return { ...row, [field]: value };
      })
    );
  }, []);

  const handleAssignBin = () => {
    if (!formData.globalBinBarcode) {
      toast.warning('Please enter a Global Bin Barcode first');
      return;
    }

    const newData = tableData.map(row => {
      if (!hasPopulatedQty(row.deliveryNoteQty) || !hasPopulatedQty(row.actualReceived)) {
        return row;
      }

      return {
        ...row,
        binBarcode: formData.globalBinBarcode
      };
    });

    setTableData(newData);

    const updatedCount = newData.filter(
      row => row.binBarcode === formData.globalBinBarcode && hasPopulatedQty(row.deliveryNoteQty) && hasPopulatedQty(row.actualReceived)
    ).length;

    if (updatedCount === 0) {
      toast.warning('No lines with both Delivery Note Qty and Actual Received populated');
      return;
    }

    toast.success(`Bin assigned to ${updatedCount} line${updatedCount > 1 ? 's' : ''}`);
  };


  const handleReceiveAll = () => {
    const newData = tableData.map(row => ({
      ...row,
      deliveryNoteQty: String(row.balanceDue),
      actualReceived: String(row.balanceDue)
    }));
    setTableData(newData);
    toast.info('Filled all quantities to match balance due');
  };

  const buildDiscrepancySummary = (lines = []) => {
    if (!lines || lines.length === 0) return '';

    const top = lines.slice(0, 5);
    const part = top
      .map(x => `${x.code || x.productCode || x.productId || 'Item'} (${x.deliveryNoteQty} vs ${x.actualReceived})`)
      .join(', ');

    if (lines.length <= 5) return part;
    return `${part} (+${lines.length - 5} more)`;
  };

  const handleSubmit = async () => {
    setValidated(true);

    if (!formData.deliveryNoteRef) {
      return;
    }

    const receiptLines = tableData.filter(x => hasReceivedQty(x.actualReceived));
    const validLines = receiptLines.filter(
      x => hasPopulatedQty(x.deliveryNoteQty) && hasPopulatedQty(x.actualReceived) && x.binBarcode?.trim() !== ''
    );

    if (receiptLines.length === 0) {
      toast.error('No items have been received');
      return;
    }

    if (validLines.length === 0) {
      toast.error('At least one item must have a valid quantity and Bin Barcode');
      return;
    }

    const proceedSubmission = async () => {
      setIsSubmitting(true);
      try {
        const payload = {
          ReceiptDate: formData.dateReceived.toISOString(),
          DelNoteRef: formData.deliveryNoteRef,
          Items: validLines.map(line => ({
            BaseUc: line.baseUc,
            TransQty: Number(line.actualReceived),
            DelNoteQty: Number(line.deliveryNoteQty),
            ProductId: line.productId,
            ProductItemId: line.productItemId,
            PoItemId: line.poItemId,
            BinBarCode: line.binBarcode
          }))
        };

        const res = await submitBookingIn(poId, payload);
        if (!res?.success) {
          throw new Error(res?.message || 'Submit failed');
        }

        // API may return string GRN, or object { grnNo: "..." }.
        const newGrnNo = extractGrnNo(res.data);
        if (!newGrnNo) {
          toast.error('Missing GRN number from server. Cannot continue to photos/email.');
          return;
        }

        setGrnNo(newGrnNo);
        // Extract numeric ID by removing non-digit characters (e.g., WGR)
        const numericId = parseInt(String(newGrnNo).replace(/[^\d]/g, ''), 10);
        if (!Number.isFinite(numericId)) {
          toast.error('Invalid GRN number format from server. Cannot continue to photos/email.');
          return;
        }
        grnInfoRef.current = { grnNo: newGrnNo, recordId: numericId };
        setRecordId(numericId);

        // Discrepancy is defined as DelNoteQty != TransQty based on the submitted list.
        // We calculate it from validLines to keep UI-only fields out of the API payload.
        const discrepancyLines = validLines.filter(
          x => Number(x.deliveryNoteQty) !== Number(x.actualReceived)
        );
        const hasDisc = discrepancyLines.length > 0;
        const discSummary = buildDiscrepancySummary(discrepancyLines);
        discrepancyInfoRef.current = { has: hasDisc, summary: discSummary };
        setHasDiscrepancy(hasDisc);
        submissionItemsRef.current = payload.Items;
        setSubmissionItems(payload.Items);

        toast.success('Transaction saved. Starting photo capture sequence.');
        promptForDeliveryNote();
      } catch (err) {
        toast.error('Error submitting booking in');
        console.error(err);
      } finally {
        setIsSubmitting(false);
      }
    };

    proceedSubmission();
  };

  const handleFinishWizard = async (incorrectItems = []) => {
    const finalGrnNo = grnInfoRef.current.grnNo || grnNo;
    const finalSubmissionItems = submissionItemsRef.current.length > 0 ? submissionItemsRef.current : submissionItems;

    if (!finalGrnNo) {
      toast.error('GRN is missing. Cannot save differences or send mail.');
      return;
    }
    setIsSubmitting(true);
    try {
      if (incorrectItems.length > 0) {
        await saveIncorrectItems(poId, finalGrnNo, incorrectItems);
      }

      // Call send-mail (last step)
      await sendMailDiscrepancy(poId, finalGrnNo, finalSubmissionItems, formData.deliveryNoteRef, formData.dateReceived);
      toast.success('Booking in process completed fully');
      handleClose();
    } catch (err) {
      toast.error('Failed to complete final steps');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.BOOKING:
        return (
          <>
            <BookingInForm
              formData={formData}
              handleInputChange={handleInputChange}
              handleDateChange={handleDateChange}
              handleAssignBin={handleAssignBin}
              handleReceiveAll={handleReceiveAll}
              isLoading={isLoading}
              tableData={tableData}
              validated={validated}
            />

            <BookingInTable
              data={tableData}
              handleRowChange={handleRowChange}
              validated={validated}
            />
          </>
        );

      case STEPS.PHOTO_DN:
        return (
          <BookingInPhotoStep
            key={APPS.DELIVERY_NOTE}
            title="Upload Delivery Note Photo"
            grnNo={grnInfoRef.current.grnNo || grnNo}
            recordId={grnInfoRef.current.recordId || recordId}
            app={APPS.DELIVERY_NOTE}
            s3Prefix={buildPhotoPrefix('sls/delivery-note-photos', grnInfoRef.current.grnNo || grnNo)}
            isOptional={true}
            onNext={() => promptForDiscrepancy()}
          />
        );

      case STEPS.PHOTO_DISC:
        return (
          <BookingInPhotoStep
            key={APPS.DISCREPANCY}
            title="Upload Discrepancy Photos"
            grnNo={grnInfoRef.current.grnNo || grnNo}
            recordId={grnInfoRef.current.recordId || recordId}
            app={APPS.DISCREPANCY}
            s3Prefix={buildPhotoPrefix('sls/discrepancy-photos', grnInfoRef.current.grnNo || grnNo)}
            isOptional={true}
            onNext={() => promptForIncorrect()}
          />
        );

      case STEPS.PHOTO_INC:
        return (
          <BookingInIncorrectStep
            grnNo={grnInfoRef.current.grnNo || grnNo}
            recordId={grnInfoRef.current.recordId || recordId}
            app={APPS.INCORRECT_ITEM}
            s3Prefix={buildPhotoPrefix('sls/incorrect-item-photos', grnInfoRef.current.grnNo || grnNo)}
            onNext={(items) => handleFinishWizard(items)}
            onCancel={() => handleFinishWizard([])}
          />
        );

      default:
        return null;
    }
  };

  const promptForDeliveryNote = () => {
    swalConfirm(
      'Delivery Note',
      'Do you want to upload any Delivery Note photos?',
      () => setCurrentStep(STEPS.PHOTO_DN),
      () => promptForDiscrepancy()
    );
  };

  const promptForDiscrepancy = () => {
    const { has, summary } = discrepancyInfoRef.current || { has: false, summary: '' };

    if (!has) {
      promptForIncorrect();
      return;
    }

    const detail = summary ? `\n\nDetected: ${summary}` : '';

    swalConfirm(
      'Discrepancy Detected',
      `Detected quantity differences (DelNoteQty != TransQty). Do you want to upload photos?${detail}`,
      () => setCurrentStep(STEPS.PHOTO_DISC),
      () => promptForIncorrect()
    );
  };

  const promptForIncorrect = () => {
    swalConfirm(
      'Differences',
      'Do you have any incorrect items (ordered A, sent B) or other differences to record?',
      () => setCurrentStep(STEPS.PHOTO_INC),
      () => handleFinishWizard([]) // If "No", just finish
    );
  };

  return (
    <Modal
      show={isShow}
      onHide={handleClose}
      size="xl"
      dialogClassName="modal-95w"
      centered
      contentClassName="border"
      backdrop="static"
    >
      <Modal.Header
        closeButton
        closeVariant={isDark ? 'white' : undefined}
        className="bg-light border-bottom"
      >
        <Modal.Title className="d-flex align-items-center w-100">
          <span className="me-3">Booking In</span>
          <span className="badge bg-primary me-2">WPO: {wpoData?.wpoNo}</span>
          <div className="ms-auto fs--1 text-600">
            Step {currentStep + 1} of {hasDiscrepancy ? 5 : 4}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="bg-100 p-4">
        {isLoading ? (
          <div className="text-center py-5">Loading...</div>
        ) : (
          renderStepContent()
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light">
        <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
        {currentStep === STEPS.BOOKING && (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading || !tableData.some(row => hasReceivedQty(row.actualReceived))}
          >
            {isSubmitting ? 'Processing...' : 'Submit Receipt'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

BookingInModal.propTypes = {
  isShow: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  wpoData: PropTypes.object
};

export default BookingInModal;
